import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getOrders } from '../services/apiClient';
import {
  selectRefundRequests, selectActive, selectHistory, mergeById, closedAt, getOrderTime,
  matchesSearch,
} from './orderSelectors';
import { getDateOffsetLocal } from '../utils/formatters';
import logger from '../utils/logger';

// Até onde o histórico vai buscar. A primeira é o que se vê sem pedir nada.
export const HISTORY_WINDOWS = [
  { key: '48h', days: 2, label: 'Últimas 48 horas' },
  { key: '30d', days: 30, label: 'Último mês' },
  { key: '6m', days: 180, label: 'Últimos 6 meses' },
  { key: '1y', days: 365, label: 'Último ano' },
  { key: 'all', days: null, label: 'Todos' },
];

const DIA_MS = 24 * 60 * 60 * 1000;

const janelaDe = (key) => HISTORY_WINDOWS.find((w) => w.key === key) || HISTORY_WINDOWS[0];

/** A janela em milissegundos, para o filtro do lado do browser. `null` = tudo. */
export const historyWindowMs = (key) => {
  const { days } = janelaDe(key);
  return days === null ? null : days * DIA_MS;
};

// O histórico só quer o que já fechou. Numa chamada só: são três estados, e
// três pedidos para o mesmo ecrã eram três vezes o custo.
const HISTORY_STATUSES = 'completed,cancelled,refunded';

// Teto por chamada nas janelas com limite. O "Todos" vai sem teto nenhum, de
// propósito — é o que o staff pediu, e um teto silencioso num ecrã que diz
// "Todos" é pior do que uma espera.
const HISTORY_PAGE = 500;

// De quanto em quanto tempo o relógio do painel avança.
//
// As secções dependem da hora, e não só dos dados: um "Novo Pedido" começa a
// piscar a meia hora da hora marcada, um pedido esquecido sai para o histórico
// uma hora depois dela. À espera do refresh de 60 segundos isso podia
// acontecer com um minuto de atraso; 30 segundos chega e sobra, e não obriga a
// redesenhar o ecrã a cada segundo.
const RELOGIO_MS = 30 * 1000;

const extractSchedulingData = (order) => {
  const metaData = order.meta_data || [];
  const pickupDate = metaData.find(item => item.key === 'wpc_pro_pickup_date')?.value;
  const pickupTime = metaData.find(item => item.key === 'wpc_pro_pickup_time')?.value;
  const deliveryDate = metaData.find(item => item.key === 'wpc_pro_delivery_date')?.value;
  const deliveryTime = metaData.find(item => item.key === 'wpc_pro_delivery_time')?.value;

  const processedOrder = { ...order };

  // Combine date and time for pickup
  if (pickupDate && pickupTime) {
    processedOrder.pickup_time = `${pickupDate}T${pickupTime}:00`;
  }

  // Combine date and time for delivery
  if (deliveryDate && deliveryTime) {
    processedOrder.delivery_time = `${deliveryDate}T${deliveryTime}:00`;
  }

  return processedOrder;
};

const sortOrdersByTimePriority = (orders) => {
  return [...orders].sort((a, b) => {
    const timeA = getOrderTime(a);
    const timeB = getOrderTime(b);
    if (timeA && timeB) return timeA.getTime() - timeB.getTime();
    if (timeA && !timeB) return -1;
    if (!timeA && timeB) return 1;
    return b.order_id - a.order_id;
  });
};

const isDateInRange = (order, selectedDate) => {
  if (!selectedDate) return true;
  const orderType = order.tipo ? order.tipo.toLowerCase() : 'pickup';
  let dateToCompare = null;
  if (orderType === 'pickup' && order.pickup_time) {
    dateToCompare = order.pickup_time;
  } else if (orderType === 'delivery' && order.delivery_time) {
    dateToCompare = order.delivery_time;
  }
  // Nem toda a encomenda tem hora marcada — as imediatas não têm. Antes essas
  // devolviam `false` e desapareciam todas assim que se escolhia um dia, o que
  // no histórico dava um ecrã vazio sem explicação. A hora a que fecharam é a
  // melhor aproximação que existe do dia a que pertencem.
  if (!dateToCompare) {
    const quando = closedAt(order);
    if (quando === null) return false;
    dateToCompare = new Date(quando);
  }
  const orderDateOnly = new Date(dateToCompare).toISOString().split('T')[0];
  const selectedDateOnly = new Date(selectedDate).toISOString().split('T')[0];
  return orderDateOnly === selectedDateOnly;
};

export const useOrders = (token, apiUrl, onLogout) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ statuses: [], restaurantes: [], selectedDate: '', search: '' });

  // A janela do histórico que está aplicada, e o texto da pesquisa.
  const [historyWindow, setHistoryWindow] = useState(HISTORY_WINDOWS[0].key);
  const [historyLoading, setHistoryLoading] = useState(false);
  const abortHistory = useRef(null);

  // A hora que as secções usam. Ver o `RELOGIO_MS`.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const relogio = setInterval(() => setNow(Date.now()), RELOGIO_MS);
    return () => clearInterval(relogio);
  }, []);

  const fetchOrders = useCallback(
    async (showLoading = true) => {
      if (!token) {
        logger.warn('fetchOrders called without token');
        return;
      }
      if (showLoading) setLoading(true);
      setError(null);
      logger.info('Fetching orders', { showLoading });
      try {
        // Três chamadas de propósito. A primeira traz as 50 mais recentes; um
        // pedido de reembolso de uma encomenda de há três dias não vem lá, e
        // nenhuma secção o poderia mostrar. A segunda vai buscá-los pelo estado
        // do pedido, e a lista é minúscula. A terceira é o histórico das 48
        // horas — o que o ecrã mostra sem ninguém pedir nada, e que num dia de
        // ponta não cabe nas 50 da primeira.
        //
        // As janelas mais largas não andam neste ciclo: carregam a pedido e
        // ficam em memória, com estas a fundir por cima. Repetir um "Todos" a
        // cada minuto era arrastar a tabela inteira sessenta vezes por hora.
        const [data, pedidos, recentes] = await Promise.all([
          getOrders(apiUrl, token),
          getOrders(apiUrl, token, { refund_request: 'requested', limit: 100 })
            .catch((err) => {
              // Não pode tirar o painel do ar: sem esta lista o staff perde os
              // pedidos antigos, com um erro perde tudo.
              logger.warn('Failed to fetch refund requests', { error: err.message });
              return { orders: [] };
            }),
          getOrders(apiUrl, token, {
            status: HISTORY_STATUSES,
            from: getDateOffsetLocal(HISTORY_WINDOWS[0].days),
            limit: HISTORY_PAGE,
          }).catch((err) => {
            logger.warn('Failed to fetch recent history', { error: err.message });
            return { orders: [] };
          }),
        ]);
        if (data.status === 'success' && data.orders) {
          const todas = mergeById(
            mergeById(data.orders, recentes.orders || []),
            pedidos.orders || []
          );
          logger.info('Orders fetched successfully', { count: todas.length });
          const processedOrders = todas.map(extractSchedulingData);
          // As janelas largas já carregadas não se perdem num refresh: o que
          // chega agora é mais fresco e ganha, o resto fica.
          setOrders((prev) => sortOrdersByTimePriority(mergeById(prev, processedOrders)));
        } else {
          logger.error('Unexpected response format from orders API', { data });
          setError('Formato de resposta inesperado do servidor');
        }
      } catch (err) {
        logger.error('Failed to fetch orders', { error: err.message, status: err.status });
        if (err.status === 401 && onLogout) {
          logger.warn('Unauthorized - logging out');
          onLogout();
        } else {
          setError(err.message || 'Erro de rede ao buscar pedidos.');
        }
      } finally {
        if (showLoading) setLoading(false);
      }
    },
    [token, apiUrl, onLogout]
  );

  /**
   * Vai buscar o histórico de uma janela mais larga e funde-o no que já cá está.
   *
   * O `AbortController` não é enfeite: o "Todos" pode trazer a tabela inteira, e
   * quem carregou no botão tem de o poder desistir sem ficar com a página presa
   * a dizer "a carregar". Um pedido novo cancela o anterior pela mesma razão —
   * duas janelas a chegar fora de ordem punham o ecrã no estado errado.
   */
  const fetchHistory = useCallback(
    async (windowKey) => {
      if (!token) {
        logger.warn('fetchHistory called without token');
        return;
      }

      abortHistory.current?.abort();
      const controller = new AbortController();
      abortHistory.current = controller;

      const janela = janelaDe(windowKey);
      setHistoryLoading(true);
      setError(null);
      logger.info('Fetching order history', { windowKey, days: janela.days });

      try {
        const data = await getOrders(
          apiUrl,
          token,
          {
            status: HISTORY_STATUSES,
            from: janela.days === null ? undefined : getDateOffsetLocal(janela.days),
            limit: janela.days === null ? 0 : HISTORY_PAGE,
          },
          { signal: controller.signal }
        );

        if (data.status === 'success' && data.orders) {
          logger.info('Order history fetched', { windowKey, count: data.orders.length });
          const processed = data.orders.map(extractSchedulingData);
          setOrders((prev) => sortOrdersByTimePriority(mergeById(prev, processed)));
          setHistoryWindow(windowKey);
        } else {
          logger.error('Unexpected response format from orders API', { data });
          setError('Formato de resposta inesperado do servidor');
        }
      } catch (err) {
        if (err.name === 'AbortError') {
          // Desistir não é falhar. Um banner vermelho aqui só assustava quem
          // carregou no "Cancelar" de propósito.
          logger.info('Order history fetch cancelled', { windowKey });
          return;
        }
        logger.error('Failed to fetch order history', { error: err.message, status: err.status });
        if (err.status === 401 && onLogout) {
          onLogout();
        } else {
          setError(err.message || 'Erro de rede ao buscar o histórico.');
        }
      } finally {
        // Só o pedido que ainda é o corrente desliga o indicador: um pedido
        // cancelado a chegar depois do que o substituiu apagava o "a carregar"
        // do que ainda estava a andar.
        if (abortHistory.current === controller) {
          abortHistory.current = null;
          setHistoryLoading(false);
        }
      }
    },
    [token, apiUrl, onLogout]
  );

  const cancelHistoryFetch = useCallback(() => {
    abortHistory.current?.abort();
    abortHistory.current = null;
    setHistoryLoading(false);
  }, []);

  useEffect(() => {
    fetchOrders(true);
    const interval = setInterval(() => fetchOrders(false), 60000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  // Um pedido a meio não pode sobreviver à página que o lançou.
  useEffect(() => () => abortHistory.current?.abort(), []);

  const handleStatusUpdate = (updatedOrder) => {
    setOrders((prevOrders) => {
      const processedOrder = extractSchedulingData(updatedOrder);
      const newOrders = prevOrders.map((order) => (order.order_id === processedOrder.order_id ? processedOrder : order));
      return sortOrdersByTimePriority(newOrders);
    });
  };

  const applyFilters = useCallback(
    (ordersToFilter) => {
      return ordersToFilter.filter((order) => {
        if (filters.statuses?.length > 0 && !filters.statuses.includes(order.app_status)) {
          return false;
        }
        if (filters.restaurantes?.length > 0) {
          const orderRestaurante = order.restaurante || '';
          const matches = filters.restaurantes.some((r) => orderRestaurante.toLowerCase().includes(r.toLowerCase()));
          if (!matches) return false;
        }
        if (filters.selectedDate && !isDateInRange(order, filters.selectedDate)) {
          return false;
        }
        // A pesquisa passa por aqui, e não só pelo histórico: o pedido de que
        // o cliente está a falar ao telefone pode estar em qualquer secção.
        if (!matchesSearch(order, filters.search)) {
          return false;
        }
        return true;
      });
    },
    [filters]
  );

  const filteredOrders = useMemo(() => applyFilters(orders), [orders, applyFilters]);

  // As três secções vivem em orderSelectors.js, testáveis como funções puras.
  const refundRequests = useMemo(
    () => sortOrdersByTimePriority(selectRefundRequests(filteredOrders)),
    [filteredOrders]
  );

  // Só a hora marcada manda na ordem, mesmo com um "Novo Pedido" a piscar: o
  // piscar já o faz ver, e a fila tem de continuar a ler-se de cima para baixo
  // como a ordem por que as coisas têm de sair.
  const activeOrders = useMemo(
    () => sortOrdersByTimePriority(selectActive(filteredOrders, now)),
    [filteredOrders, now]
  );

  // O histórico já sai ordenado pela hora a que cada encomenda lá entrou — não
  // passa pelo `sortOrdersByTimePriority`, que ordena por hora de levantamento
  // e era o que punha a encomenda de ontem à noite à frente da que acabou de
  // sair.
  //
  // As atrasadas ainda abertas chegam pela chamada principal: as das janelas
  // largas só pedem estados fechados, e uma aberta de há três dias já não é
  // trabalho de ninguém.
  const historyOrders = useMemo(
    () => selectHistory(filteredOrders, {
      now,
      windowMs: historyWindowMs(historyWindow),
    }),
    [filteredOrders, historyWindow, now]
  );

  return {
    orders,
    loading,
    error,
    filters,
    setFilters,
    fetchOrders,
    handleStatusUpdate,
    filteredOrders,
    refundRequests,
    activeOrders,
    historyOrders,
    now,
    historyWindow,
    historyLoading,
    fetchHistory,
    cancelHistoryFetch,
  };
};
